import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, Users, FileText, Factory, Receipt, ShoppingCart, X } from "lucide-react";
import { usePerms } from "@/PermissionContext";
import { useIsMobile } from "@/hooks/useBreakpoint";

/**
 * Shared quick-create menu. The app places it in the top bar so it cannot
 * cover page controls or totals; floating placement remains available.
 * Uses permission checks so items hide for users who can't create them.
 */
export default function QuickActionFab({ placement = "floating" }) {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { can } = usePerms();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const inHeader = placement === "header";

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const key = e => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    if (open) document.addEventListener("mousedown", h);
    if (open) document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", h);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  useEffect(() => { setOpen(false); }, [pathname]);

  const items = [
    { icon: Users,        label: "New customer",       to: "/customers?new=1",     perm: ["customers", "create"] },
    { icon: FileText,     label: "New quotation",      to: "/quotations/new",      perm: ["quotations", "create"] },
    { icon: Factory,      label: "New work order",     to: "/work-orders?new=1",   perm: ["work_orders", "create"] },
    { icon: FileText,     label: "New sales invoice",  to: "/invoices/new",        perm: ["invoices", "create"] },
    { icon: Receipt,      label: "New receipt",        to: "/receipts/new",        perm: ["receipts", "create"] },
    { icon: ShoppingCart, label: "New purchase order", to: "/purchase-orders/new", perm: ["purchase_orders", "create"] },
  ].filter(it => can(...it.perm));

  if (items.length === 0) return null;   // nothing user is allowed to create
  // A fixed shortcut can obscure financial totals anywhere in a phone document.
  // Keep document actions in the header and leave desktop shortcuts unchanged.
  if (isMobile && /^\/proformas\/(?!new\/?$)[^/]+\/?$/.test(pathname)) return null;

  return (
    <div ref={ref} style={{
      position: inHeader ? "relative" : "fixed",
      right: inHeader ? undefined : 32,
      bottom: inHeader ? undefined : (isMobile ? "var(--flg-fab-bottom-mobile)" : 32),
      flexShrink: 0,
      zIndex: "var(--flg-z-drawer)",
    }} data-testid="fab-root" data-placement={placement}>
      {open && (
        <div
          data-testid="fab-menu"
          style={{
            position: "absolute", bottom: inHeader ? undefined : 68,
            top: inHeader ? "calc(100% + 8px)" : undefined, right: 0,
            background: "var(--flg-bg-elevated)",
            border: "1px solid var(--flg-border)",
            borderRadius: "var(--flg-radius-lg)",
            padding: 6,
            boxShadow: "var(--flg-shadow-xl)",
            width: "min(240px, calc(100vw - 28px))",
            maxHeight: "calc(100dvh - 96px)", overflowY: "auto",
            animation: "f-fab-in 180ms cubic-bezier(0.2, 0.7, 0.1, 1)",
          }}
        >
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <button key={i} type="button"
                data-testid={`fab-item-${i}`}
                onClick={() => { setOpen(false); nav(it.to); }}
                style={{
                  display: "flex", gap: 12, alignItems: "center",
                  padding: "10px 12px", borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 14, fontWeight: 500,
                  color: "var(--flg-fg)",
                  width: "100%", border: 0, background: "transparent", textAlign: "left",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--flg-bg-subtle)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <div style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: "var(--flg-bg-muted)", color: "var(--flg-fg-secondary)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}><Icon size={15} /></div>
                {it.label}
              </button>
            );
          })}
        </div>
      )}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="fab-button"
        aria-label="Quick action menu"
        aria-expanded={open}
        style={{
          width: inHeader ? 36 : 56, height: inHeader ? 36 : 56,
          borderRadius: inHeader ? 8 : "50%",
          border: 0, cursor: "pointer",
          background: "var(--flg-primary)", color: "white",
          boxShadow: inHeader ? "none" : "0 8px 24px rgba(214,58,58,0.32), 0 2px 4px rgba(0,0,0,0.08)",
          display: "flex", alignItems: "center", justifyContent: "center",
          transform: open ? "rotate(45deg)" : "rotate(0)",
          transition: "transform 180ms cubic-bezier(0.4,0,0.2,1), background 180ms",
        }}
        onMouseEnter={e => e.currentTarget.style.background = "var(--flg-primary-hover)"}
        onMouseLeave={e => e.currentTarget.style.background = "var(--flg-primary)"}
      >
        {open ? <X size={22} strokeWidth={2.4} /> : <Plus size={22} strokeWidth={2.4} />}
      </button>
      <style>{`@keyframes f-fab-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
